import { useCallback, useMemo, useState } from 'react'
import type { SelectChangeEvent } from '@mui/material/Select'
import { validateName } from '@/common/utils'
import type { EnvironmentValue, ExpirationOptionValue } from '../constants'
import { DEFAULT_ENVIRONMENT } from '../constants'

export interface ApiKeyFormInitialValues {
  readonly name?: string
  readonly environment?: EnvironmentValue
  readonly expirationDays?: ExpirationOptionValue
  readonly customExpirationDate?: Date | null
}

export interface UseApiKeyFormReturn {
  readonly name: string
  readonly environment: EnvironmentValue
  readonly expirationDays: ExpirationOptionValue
  readonly customDate: Date | null
  readonly touched: boolean
  readonly validation: { readonly isValid: boolean; readonly error: string | null }
  readonly isCustomDateRequired: boolean
  readonly isFormValid: boolean
  readonly handleNameChange: (event: { target: { value: string } }) => void
  readonly handleEnvironmentChange: (event: SelectChangeEvent) => void
  readonly handleExpirationChange: (event: SelectChangeEvent) => void
  readonly handleCustomDateChange: (newValue: Date | null) => void
}

export function useApiKeyForm(initialValues?: ApiKeyFormInitialValues): UseApiKeyFormReturn {
  const [name, setName] = useState(initialValues?.name ?? '')
  const [environment, setEnvironment] = useState<EnvironmentValue>(
    initialValues?.environment ?? DEFAULT_ENVIRONMENT,
  )
  const [expirationDays, setExpirationDays] = useState<ExpirationOptionValue>(
    initialValues?.expirationDays ?? 30,
  )
  const [customDate, setCustomDate] = useState<Date | null>(
    initialValues?.customExpirationDate ?? null,
  )
  const [touched, setTouched] = useState(false)

  const validation = useMemo(() => validateName(name, 'Key name'), [name])

  const isCustomDateRequired = useMemo(() => expirationDays === 'custom', [expirationDays])

  const isFormValid = useMemo(
    () => validation.isValid && (!isCustomDateRequired || customDate !== null),
    [validation.isValid, isCustomDateRequired, customDate],
  )

  const handleNameChange = useCallback((event: { target: { value: string } }) => {
    setName(event.target.value)
    setTouched(true)
  }, [])

  const handleEnvironmentChange = useCallback((event: SelectChangeEvent) => {
    setEnvironment(event.target.value as EnvironmentValue)
  }, [])

  const handleExpirationChange = useCallback((event: SelectChangeEvent) => {
    const value = event.target.value
    if (value === 'null') {
      setExpirationDays(null)
    } else if (value === 'custom') {
      setExpirationDays('custom')
    } else {
      setExpirationDays(Number(value))
    }
  }, [])

  const handleCustomDateChange = useCallback((newValue: Date | null) => {
    setCustomDate(newValue)
  }, [])

  return {
    name,
    environment,
    expirationDays,
    customDate,
    touched,
    validation,
    isCustomDateRequired,
    isFormValid,
    handleNameChange,
    handleEnvironmentChange,
    handleExpirationChange,
    handleCustomDateChange,
  }
}
